/**
 * The reason form for park, DNA, cancel and reopen (docs/ux/matter-hub.md
 * "Forms"): a Dialog on desktop, a Sheet on the phone. It previews the from
 * and to chips, the appointment and the flags the engine will raise, asks
 * for the reason (and the outstanding items for a park), shows a 409 inline
 * when the matter changed while the form was open, and never fires twice.
 */

import { EVENT_DISPLAY, eventLabel, statusLabel, type Status, type StatusEvent } from '@beam/core/status';
import { cn } from 'cn';
import { PlusIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { Appointment, TransitionOption } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { FlagText } from '@/components/matter/FlagText';
import { StatusChip } from '@/components/matter/StatusChip';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { dateTime, localDateKey } from '@/lib/format';
import { CONFIRM_LABEL, DESTRUCTIVE_KINDS, DISMISS_LABEL } from './actions';
import { CONFLICT_TEXT } from './useMatter';

export type ItemDraft = { description: string; followUpAt: string };

export type TransitionFormProps = {
  matterRef: string;
  from: Status;
  option: TransitionOption | null;
  appointment?: Appointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  conflict: boolean;
  offline: boolean;
  onSubmit: (values: { event: StatusEvent['kind']; reason: string; items?: ItemDraft[] }) => void;
};

const REASON_MAX = 500;

function defaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return localDateKey(d);
}

const today = (): string => localDateKey();

export function TransitionForm(props: TransitionFormProps) {
  const phone = useIsPhone();
  const { open, onOpenChange, option } = props;
  if (!option) return null;
  // The sheet has no horizontal padding of its own; the dialog does.
  const body = <FormBody key={`${option.kind}-${String(open)}`} {...props} option={option} className={phone ? 'px-4' : undefined} />;
  const title = eventLabel(option.kind);
  const description = <Sentence {...props} option={option} />;
  if (phone) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[92svh] overflow-y-auto rounded-t-[20px] shadow-beam-3" onOpenAutoFocus={(e) => { e.preventDefault(); (document.getElementById('transition-reason') as HTMLTextAreaElement | null)?.focus(); }}>
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" onOpenAutoFocus={(e) => { e.preventDefault(); (document.getElementById('transition-reason') as HTMLTextAreaElement | null)?.focus(); }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function Sentence({ matterRef, from, option, appointment }: Pick<TransitionFormProps, 'matterRef' | 'from' | 'appointment'> & { option: TransitionOption }) {
  const to = option.to ?? (option.kind === 'cancel' ? 15 : option.kind === 'dna' ? 14 : option.kind === 'park_awaiting_information' ? 13 : 9);
  return (
    <span className="flex flex-col gap-1 text-sm text-foreground">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="tabular font-semibold">{matterRef}</span> moves from <StatusChip status={from} size="sm" /> to <StatusChip status={to as Status} size="sm" />.
        {option.kind === 'park_awaiting_information' ? <span>Resume returns it to <StatusChip status={from} size="sm" />.</span> : null}
        {option.kind === 'cancel' ? <span>15 {statusLabel(15)} is terminal: the matter cannot be reopened.</span> : null}
      </span>
      {appointment && (option.kind === 'dna' || option.kind === 'cancel') ? (
        <span className="text-beam-text-muted">Appointment: {dateTime(appointment.startsAt)}, {appointment.clinicName}{hoursAway(appointment.startsAt)}.</span>
      ) : null}
      <FlagsPreview option={option} />
    </span>
  );
}

function hoursAway(iso: string): string {
  const h = Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);
  return h > 0 && h <= 96 ? `, in ${h} hours` : '';
}

function FlagsPreview({ option }: { option: TransitionOption }) {
  const f = option.flags ?? {};
  const raised: ReactNode[] = [];
  if (f.repeatDna) raised.push(<span key="r"><FlagText kind="repeatDna" /> (a prior DNA is on this matter)</span>);
  if (f.dnaFeeSuggested) raised.push(<span key="d"><FlagText kind="dnaFeeSuggested" />. The fee itself is handled outside the portal</span>);
  if (f.lateCancellation) raised.push(<span key="l"><FlagText kind="lateCancellation" /> (inside the 72 hour window)</span>);
  if (raised.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-1 text-beam-text-muted">
      The engine will flag: {raised.map((r, i) => <span key={i} className="inline-flex items-center gap-1">{r}{i < raised.length - 1 ? ',' : '.'}</span>)}
    </span>
  );
}

function FormBody({ option, busy, conflict, offline, onSubmit, onOpenChange, className }: TransitionFormProps & { option: TransitionOption; className?: string }) {
  const [reason, setReason] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([{ description: '', followUpAt: defaultFollowUp() }]);
  const [errors, setErrors] = useState<{ reason?: string; items: Array<{ description?: string; followUpAt?: string }> }>({ items: [] });
  const [submitted, setSubmitted] = useState(false);
  const id = useId();
  const firstInvalid = useRef<HTMLElement | null>(null);
  const isPark = option.kind === 'park_awaiting_information';
  const destructive = DESTRUCTIVE_KINDS.has(option.kind);

  useEffect(() => {
    if (submitted) firstInvalid.current?.focus();
  }, [submitted, errors]);

  function validate(): boolean {
    const next: typeof errors = { items: [] };
    firstInvalid.current = null;
    const trimmed = reason.trim();
    if (trimmed.length === 0) next.reason = 'A reason is required';
    else if (trimmed.length > REASON_MAX) next.reason = `Keep the reason to ${REASON_MAX} characters`;
    if (isPark) {
      items.forEach((it, i) => {
        const e: { description?: string; followUpAt?: string } = {};
        if (it.description.trim().length === 0 || it.description.trim().length > 200) e.description = 'Describe what is outstanding';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(it.followUpAt) || it.followUpAt < today()) e.followUpAt = 'Choose today or a later date';
        next.items[i] = e;
      });
    }
    setErrors(next);
    const ok = !next.reason && next.items.every((e) => !e.description && !e.followUpAt);
    if (!ok) {
      const el = next.reason ? document.getElementById('transition-reason') : document.querySelector<HTMLElement>('[data-invalid="true"]');
      firstInvalid.current = el;
    }
    return ok;
  }

  function submit(e?: FormEvent) {
    e?.preventDefault();
    setSubmitted(true);
    if (!validate()) return;
    onSubmit({ event: option.kind, reason: reason.trim(), ...(isPark ? { items: items.map((i) => ({ description: i.description.trim(), followUpAt: i.followUpAt })) } : {}) });
  }

  const confirmLabel = busy ? EVENT_DISPLAY[option.kind].busy : (CONFIRM_LABEL[option.kind] ?? eventLabel(option.kind));

  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
      }}
      className={cn('flex flex-col gap-4', className)}
      noValidate
    >
      {conflict ? (
        <Alert role="alert" variant="destructive">
          <AlertTitle>{CONFLICT_TEXT}</AlertTitle>
        </Alert>
      ) : null}
      {!option.allowed ? (
        <Alert role="alert">
          <AlertTitle>This move is no longer allowed: {option.reason}</AlertTitle>
        </Alert>
      ) : null}
      <div className="flex flex-col gap-2">
        <Label htmlFor="transition-reason">Reason</Label>
        <Textarea
          id="transition-reason"
          name="reason"
          rows={3}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={errors.reason ? true : undefined}
          aria-describedby={errors.reason ? `${id}-reason-error` : reason.length >= 400 ? `${id}-counter` : undefined}
          className="min-h-[72px] text-base md:text-sm"
        />
        {errors.reason ? <p id={`${id}-reason-error`} className="text-[13px] text-beam-error">{errors.reason}</p> : null}
        {reason.length >= 400 ? <p id={`${id}-counter`} className="tabular text-xs text-beam-text-muted">{reason.length} of {REASON_MAX}</p> : null}
      </div>
      {isPark ? (
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-medium">Outstanding items (at least one)</legend>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr_auto_auto] items-start gap-2">
              <span className="tabular pt-2 text-xs text-beam-text-muted">{i + 1}</span>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${id}-desc-${i}`} className="eyebrow text-beam-text-muted">Description</Label>
                <Input
                  id={`${id}-desc-${i}`}
                  value={it.description}
                  onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                  data-invalid={errors.items[i]?.description ? 'true' : undefined}
                  aria-invalid={errors.items[i]?.description ? true : undefined}
                  className="h-11 text-base md:h-9 md:text-sm"
                />
                {errors.items[i]?.description ? <p className="text-[13px] text-beam-error">{errors.items[i]!.description}</p> : null}
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${id}-date-${i}`} className="eyebrow text-beam-text-muted">Follow up by</Label>
                <Input
                  id={`${id}-date-${i}`}
                  type="date"
                  value={it.followUpAt}
                  min={today()}
                  onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, followUpAt: e.target.value } : x)))}
                  data-invalid={errors.items[i]?.followUpAt ? 'true' : undefined}
                  aria-invalid={errors.items[i]?.followUpAt ? true : undefined}
                  className="h-11 text-base md:h-9 md:text-sm"
                />
                {errors.items[i]?.followUpAt ? <p className="text-[13px] text-beam-error">{errors.items[i]!.followUpAt}</p> : null}
              </div>
              <Button type="button" variant="ghost" size="default" className="mt-5 h-11 md:h-8" aria-label={`Remove item ${i + 1}`} disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))}>
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="link"
            className="h-11 w-fit p-0 md:h-auto"
            onClick={() => {
              const next = items.length;
              setItems([...items, { description: '', followUpAt: defaultFollowUp() }]);
              requestAnimationFrame(() => document.getElementById(`${id}-desc-${next}`)?.focus());
            }}
          >
            <PlusIcon aria-hidden="true" data-icon="inline-start" /> Add another item
          </Button>
        </fieldset>
      ) : null}
      <FormButtons
        dismiss={DISMISS_LABEL[option.kind] ?? 'Keep as is'}
        confirm={confirmLabel}
        destructive={destructive}
        busy={busy}
        disabled={offline || !option.allowed}
        note={offline ? 'You are offline.' : null}
        onDismiss={() => onOpenChange(false)}
      />
    </form>
  );
}

function FormButtons({ dismiss, confirm, destructive, busy, disabled, note, onDismiss }: { dismiss: string; confirm: string; destructive: boolean; busy: boolean; disabled: boolean; note: string | null; onDismiss: () => void }) {
  const phone = useIsPhone();
  const Footer = phone ? SheetFooter : DialogFooter;
  return (
    <Footer className={cn('flex-row justify-end gap-2 p-0 pt-2', phone && 'sticky bottom-0 bg-beam-paper pb-4')}>
      <Button type="button" variant="outline" size="lg" className="h-11 md:h-9" onClick={onDismiss}>{dismiss}</Button>
      <Button
        type="submit"
        size="lg"
        variant={destructive ? 'destructive' : 'default'}
        data-variant={destructive ? 'destructive' : 'default'}
        disabled={busy || disabled}
        className={cn('h-11 md:h-9', destructive && 'bg-beam-error text-beam-paper hover:bg-beam-error/90')}
      >
        {confirm}
      </Button>
      {note ? <p className="w-full text-right text-xs text-beam-text-muted">{note}</p> : null}
    </Footer>
  );
}
